import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { KeyboardArrowDown, KeyboardArrowRight, WarningAmber } from "@mui/icons-material";
import apiService from "../../services/api";
import { createLogger, adminLogContext } from '../../utils/logger'
import { isSessionExpired, isSessionExpiredError, subscribeSessionExpired } from '../../utils/sessionAuth'
import ScrapingRunStats, { buildRunStats, formatRunDuration } from "./ScrapingRunStats";
import { addUserActionBreadcrumb } from "../../utils/sentryTracking";

const logger = createLogger('ScrapingManager')
const ADMIN_TAB = 'scraping'
const ctx = (extra) => adminLogContext(ADMIN_TAB, extra)


const ScrapingManager = () => {
  const [counties, setCounties] = useState([]);
  const [selectedCountyId, setSelectedCountyId] = useState("");

  const [regions, setRegions] = useState([]);
  const [countyTasks, setCountyTasks] = useState([]);

  const [loadingCounties, setLoadingCounties] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [queueingRegion, setQueueingRegion] = useState(null); // track which region is queueing
  const [retryingRegion, setRetryingRegion] = useState(null); // track which region is clearing failures
  const [queueingCounty, setQueueingCounty] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [completionStatus, setCompletionStatus] = useState({}); // county_id -> boolean (all sources scraped)
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [manifestData, setManifestData] = useState(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [runConfigOverride, setRunConfigOverride] = useState(null); // null = use defaults
  const [showRunConfig, setShowRunConfig] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(isSessionExpired());

  useEffect(() => {
    return subscribeSessionExpired(() => setSessionExpired(true));
  }, []);

  const isHeartbeatStale = (task) => {
    if (task.status !== "running" || !task.last_heartbeat_at) return false;
    return Date.now() - new Date(task.last_heartbeat_at).getTime() > 10 * 60 * 1000;
  };

  const handleExpandTask = async (taskId) => {
    if (expandedTaskId === taskId) {
      setExpandedTaskId(null);
      setManifestData(null);
      return;
    }
    setExpandedTaskId(taskId);
    setManifestData(null);
    try {
      setLoadingManifest(true);
      const data = await apiService.getScrapingTaskManifest(taskId);
      setManifestData(data);
    } catch (err) {
      logger.apiFailure('Failed to load manifest', ctx({ action: 'load_manifest', task_id: String(taskId) }), err);
    } finally {
      setLoadingManifest(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      const t0 = performance.now();
      try {
        setLoadingCounties(true);
        setError("");
        const [countiesResp, completionResp] = await Promise.all([
          apiService.getAllCounties(),
          apiService.getCountiesScrapingCompletionStatus(),
        ]);
        setCounties(countiesResp.counties || []);
        setCompletionStatus(completionResp.status || {});
        logger.info('Scraping manager counties loaded', ctx({
          action: 'load_counties',
          county_count: (countiesResp.counties || []).length,
          load_time_ms: Math.round(performance.now() - t0),
        }));
      } catch (err) {
        logger.apiFailure('Failed to load counties or completion status', ctx({ action: 'load_counties' }), err);
        setError("Failed to load counties.");
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchInitialData();
  }, []);

  // Auto-refresh tasks every 10 seconds when a county is selected
  useEffect(() => {
    if (!selectedCountyId || sessionExpired) return;

    const intervalId = setInterval(() => {
      if (isSessionExpired()) return;
      // Silently refresh tasks and completion status (don't set loading state to avoid UI flicker)
      Promise.all([
        apiService.getAllScrapingTasksForCounty(selectedCountyId),
        apiService.getCountiesScrapingCompletionStatus(),
      ])
        .then(([tasks, completionResp]) => {
          setCountyTasks(tasks || []);
          setCompletionStatus(completionResp.status || {});
        })
        .catch((err) => {
          if (isSessionExpiredError(err)) return;
          logger.apiFailure('Failed to auto-refresh scraping tasks', ctx({
            action: 'poll_scraping_tasks',
            county_id: String(selectedCountyId),
          }), err);
        });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [selectedCountyId, sessionExpired]);

  const handleCountyChange = async (event) => {
    const countyId = event.target.value;
    setSelectedCountyId(countyId);
    setRegions([]);
    setCountyTasks([]);
    setSuccessMessage("");
    setError("");

    if (!countyId) return;

    try {
      setLoadingRegions(true);
      const data = await apiService.getScrapingRegions(countyId);
      setRegions(data || []);
    } catch (err) {
      logger.apiFailure('Failed to load scraping regions', ctx({
        action: 'load_scraping_regions',
        county_id: String(countyId),
      }), err);
      setError(
        err?.message || "Failed to load scraping regions for this county.",
      );
    } finally {
      setLoadingRegions(false);
    }

    // Also fetch all tasks for the county
    await fetchCountyTasks(countyId);
  };

  const fetchCountyTasks = async (countyId) => {
    if (!countyId) return;
    const t0 = performance.now();
    try {
      setLoadingTasks(true);
      const [tasks, completionResp] = await Promise.all([
        apiService.getAllScrapingTasksForCounty(countyId),
        apiService.getCountiesScrapingCompletionStatus(),
      ]);
      setCountyTasks(tasks || []);
      setCompletionStatus(completionResp.status || {});
      logger.info('Scraping tasks loaded', ctx({
        action: 'load_scraping_tasks',
        county_id: String(countyId),
        result_count: (tasks || []).length,
        load_time_ms: Math.round(performance.now() - t0),
      }));
    } catch (err) {
      logger.apiFailure('Failed to load county scraping tasks', ctx({
        action: 'load_scraping_tasks',
        county_id: String(countyId),
      }), err);
      setError(err?.message || "Failed to load scraping tasks.");
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleQueueRegion = async (region, sourceSystem = null) => {
    if (!region?.scraping_data_id) {
      setError(`Region "${region.name}" does not have a saved source configuration yet.`);
      return;
    }
    const regionKey = region.scraping_data_id + (sourceSystem || "all");
    try {
      setQueueingRegion(regionKey);
      setError("");
      setSuccessMessage("");
      addUserActionBreadcrumb('Queued scraping task', {
        county_id: String(selectedCountyId),
        scraping_data_id: String(region.scraping_data_id),
        source_system: sourceSystem || 'all',
        region_name: region.name,
      });
      await apiService.createScrapingTasks(region.scraping_data_id, {
        sourceSystem,
        config: runConfigOverride || undefined,
      });
      const msg = sourceSystem
        ? `Queued ${sourceSystem} for ${region.name}.`
        : `Queued all sources for ${region.name}.`;
      setSuccessMessage(msg);
      await fetchCountyTasks(selectedCountyId);
    } catch (err) {
      logger.apiFailure('Failed to queue scraping task', ctx({
        action: 'queue_scraping_task',
        county_id: String(selectedCountyId),
        scraping_data_id: region.scraping_data_id ? String(region.scraping_data_id) : undefined,
        source_system: sourceSystem || 'all',
      }), err);
      setError(err?.message || "Failed to queue scraping task.");
    } finally {
      setQueueingRegion(null);
    }
  };

  const handleQueueAllForCounty = async () => {
    if (!selectedCountyId) {
      setError("Please select a county first.");
      return;
    }
    try {
      setQueueingCounty(true);
      setError("");
      setSuccessMessage("");
      addUserActionBreadcrumb('Queued all scraping tasks for county', {
        county_id: String(selectedCountyId),
      });
      const result = await apiService.queueAllScrapingTasksForCounty(
        selectedCountyId,
        runConfigOverride || undefined,
      );
      setSuccessMessage(
        `Queued ${result.total_tasks_created} tasks across ${result.regions_queued} regions.`,
      );
      await fetchCountyTasks(selectedCountyId);
    } catch (err) {
      logger.apiFailure('Failed to queue scraping tasks for county', ctx({
        action: 'queue_all_scraping_tasks',
        county_id: String(selectedCountyId),
      }), err);
      setError(err?.message || "Failed to queue scraping tasks for county.");
    } finally {
      setQueueingCounty(false);
    }
  };

  const handleRefreshTasks = () => {
    if (selectedCountyId) {
      fetchCountyTasks(selectedCountyId);
    }
  };

  const handleRetryFailed = async (region, statuses) => {
    if (!region.scraping_data_id) return;
    const key = `${region.scraping_data_id}:${statuses.join(",")}`;
    try {
      setRetryingRegion(key);
      setError("");
      setSuccessMessage("");
      addUserActionBreadcrumb('Retry failed scraping URLs', {
        county_id: String(selectedCountyId),
        scraping_data_id: String(region.scraping_data_id),
        statuses: statuses.join(','),
      });
      const result = await apiService.retryFailedScrapingUrls(region.scraping_data_id, statuses);
      const label = statuses.length === 1
        ? (statuses[0] === "download_failed" ? "download" : "upload")
        : "download + upload";
      setSuccessMessage(
        `Cleared ${result.cleared} failed ${label} URL(s) for ${region.name}. Queue a run to retry.`,
      );
    } catch (err) {
      logger.apiFailure('Failed to clear failed URLs for retry', ctx({
        action: 'retry_failed_scraping_urls',
        county_id: String(selectedCountyId),
        scraping_data_id: region.scraping_data_id ? String(region.scraping_data_id) : undefined,
      }), err);
      setError(err?.message || "Failed to clear failed URLs for retry.");
    } finally {
      setRetryingRegion(null);
    }
  };

  const hasAnySources = (region) =>
    region.legistar_client || region.civicclerk_site_id;

  const usableRegions = regions.filter(hasAnySources);

  // Render inline queue buttons for a region
  const renderQueueButtons = (region) => {
    if (!region.scraping_data_id) {
      return (
        <Typography variant="caption" color="text.secondary">
          Not configured
        </Typography>
      );
    }

    if (!hasAnySources(region)) {
      return (
        <Typography variant="caption" color="text.secondary">
          No sources
        </Typography>
      );
    }

    const buttons = [];
    const isQueueingThis = queueingRegion?.startsWith(region.scraping_data_id);

    // "All" button
    buttons.push(
      <Button
        key="all"
        variant="contained"
        size="small"
        disabled={isQueueingThis || queueingCounty}
        onClick={() => handleQueueRegion(region)}
        sx={{ mr: 0.5, minWidth: 50 }}
      >
        All
      </Button>,
    );

    // Per-source buttons
    if (region.legistar_client) {
      buttons.push(
        <Button
          key="legistar"
          variant="outlined"
          size="small"
          disabled={isQueueingThis || queueingCounty}
          onClick={() => handleQueueRegion(region, "legistar")}
          sx={{ mr: 0.5, minWidth: 70 }}
        >
          Legistar
        </Button>,
      );
    }
    if (region.civicclerk_site_id) {
      buttons.push(
        <Button
          key="civicclerk"
          variant="outlined"
          size="small"
          disabled={isQueueingThis || queueingCounty}
          onClick={() => handleQueueRegion(region, "civicclerk")}
          sx={{ mr: 0.5, minWidth: 80 }}
        >
          CivicClerk
        </Button>,
      );
    }

    return <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{buttons}</Box>;
  };

  const renderRetryButtons = (region) => {
    if (!region.scraping_data_id) {
      return (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      );
    }

    const dlKey = `${region.scraping_data_id}:download_failed`;
    const upKey = `${region.scraping_data_id}:upload_failed`;
    const isRetrying = retryingRegion?.startsWith(region.scraping_data_id);

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <Tooltip title="Clear download_failed URLs so the next run re-downloads them">
          <span>
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={isRetrying || queueingCounty}
              onClick={() => handleRetryFailed(region, ["download_failed"])}
              sx={{ minWidth: 56 }}
            >
              {retryingRegion === dlKey ? <CircularProgress size={16} /> : "DL"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Clear upload_failed URLs so the next run re-uploads them">
          <span>
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={isRetrying || queueingCounty}
              onClick={() => handleRetryFailed(region, ["upload_failed"])}
              sx={{ minWidth: 56 }}
            >
              {retryingRegion === upKey ? <CircularProgress size={16} /> : "Up"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const statCell = (value, sx = {}) => (
    <TableCell sx={{ fontVariantNumeric: "tabular-nums", ...sx }}>
      {value > 0 ? value : ""}
    </TableCell>
  );

  const TASK_COL_COUNT = 16;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Scraping Jobs
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Queue scraping jobs for configured sources and monitor their status as
        the scraper worker polls the task table.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="county-select-label">County</InputLabel>
          <Select
            labelId="county-select-label"
            label="County"
            value={selectedCountyId}
            onChange={handleCountyChange}
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: { zIndex: 9999 },
              },
            }}
          >
            {loadingCounties && (
              <MenuItem value="">
                <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
              </MenuItem>
            )}
            {!loadingCounties &&
              counties.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box sx={{ flexGrow: 1 }}>{c.name} ({c.state})</Box>
                    <Tooltip
                      title={
                        completionStatus[c.id]
                          ? "All sources scraped"
                          : "Not all sources scraped"
                      }
                    >
                      {completionStatus[c.id] ? (
                        <CheckCircleIcon sx={{ color: "success.main", fontSize: 18, ml: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: "grey.400", fontSize: 18, ml: 1 }} />
                      )}
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="secondary"
          disabled={!selectedCountyId || queueingCounty || loadingRegions}
          onClick={handleQueueAllForCounty}
          sx={{ ml: 2 }}
        >
          {queueingCounty ? "Queueing…" : "Queue All for County"}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => setShowRunConfig((v) => !v)}
          sx={{ ml: 1 }}
        >
          {showRunConfig ? "Hide" : "Run Config"}
          {runConfigOverride && <Chip label="override" size="small" color="warning" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />}
        </Button>
      </Paper>

      <Collapse in={showRunConfig}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Per-Run Config Override
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Set overrides here before queueing. Only changed values are applied — everything else uses the global default.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              label="Days back"
              size="small"
              type="number"
              sx={{ width: 130 }}
              value={runConfigOverride?.scraping?.days_back ?? ''}
              placeholder="100"
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setRunConfigOverride((prev) => {
                    if (!prev) return null;
                    const next = { ...prev };
                    if (next.scraping) { delete next.scraping.days_back; if (Object.keys(next.scraping).length === 0) delete next.scraping; }
                    return Object.keys(next).length === 0 ? null : next;
                  });
                } else {
                  setRunConfigOverride((prev) => ({
                    ...prev,
                    scraping: { ...(prev?.scraping || {}), days_back: Number(val) },
                  }));
                }
              }}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Download timeout (s)"
              size="small"
              type="number"
              sx={{ width: 170 }}
              value={runConfigOverride?.scraping?.download_timeout_s ?? ''}
              placeholder="120"
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setRunConfigOverride((prev) => {
                    if (!prev) return null;
                    const next = { ...prev };
                    if (next.scraping) { delete next.scraping.download_timeout_s; if (Object.keys(next.scraping).length === 0) delete next.scraping; }
                    return Object.keys(next).length === 0 ? null : next;
                  });
                } else {
                  setRunConfigOverride((prev) => ({
                    ...prev,
                    scraping: { ...(prev?.scraping || {}), download_timeout_s: Number(val) },
                  }));
                }
              }}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Max retries"
              size="small"
              type="number"
              sx={{ width: 120 }}
              value={runConfigOverride?.scraping?.download_max_retries ?? ''}
              placeholder="3"
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setRunConfigOverride((prev) => {
                    if (!prev) return null;
                    const next = { ...prev };
                    if (next.scraping) { delete next.scraping.download_max_retries; if (Object.keys(next.scraping).length === 0) delete next.scraping; }
                    return Object.keys(next).length === 0 ? null : next;
                  });
                } else {
                  setRunConfigOverride((prev) => ({
                    ...prev,
                    scraping: { ...(prev?.scraping || {}), download_max_retries: Number(val) },
                  }));
                }
              }}
              inputProps={{ min: 0 }}
            />
            <Button
              variant="text"
              size="small"
              color="error"
              disabled={!runConfigOverride}
              onClick={() => setRunConfigOverride(null)}
            >
              Clear overrides
            </Button>
          </Box>
          {runConfigOverride && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', fontSize: 11 }}>
              Will apply: {JSON.stringify(runConfigOverride)}
            </Typography>
          )}
        </Paper>
      </Collapse>

      {/* Combined Regions + Tasks Panel */}
      <Paper sx={{ p: 2 }}>
        {/* Regions Section */}
        <Typography variant="subtitle1" gutterBottom>
          Regions in County
        </Typography>
        {loadingRegions && <LinearProgress sx={{ mb: 1 }} />}
        {!loadingRegions && !selectedCountyId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a county to see its regions.
          </Typography>
        ) : usableRegions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No regions with configured sources.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Region</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Queue Tasks</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Retry Failed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usableRegions.map((region) => {
                const key =
                  region.municipality_geoid ||
                  `unincorporated-${region.county_id}`;
                return (
                  <TableRow key={key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {region.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderQueueButtons(region)}</TableCell>
                    <TableCell>{renderRetryButtons(region)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Tasks Section */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">All Tasks for County</Typography>
          <Tooltip title="Refresh tasks">
            <IconButton
              size="small"
              onClick={handleRefreshTasks}
              disabled={!selectedCountyId || loadingTasks}
              sx={{ ml: 1 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {loadingTasks && <LinearProgress sx={{ mb: 1 }} />}
        {!loadingTasks && countyTasks.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {selectedCountyId
              ? "No scraping tasks yet for this county."
              : "Select a county to see tasks."}
          </Typography>
        )}
        {countyTasks.length > 0 && (
          <Table sx={{ "& th, & td": { px: 1, py: 0.75, fontSize: "0.875rem" } }}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ width: 28 }} />
                <TableCell rowSpan={2}>Region</TableCell>
                <TableCell rowSpan={2}>Source</TableCell>
                <TableCell rowSpan={2}>Status</TableCell>
                <TableCell colSpan={3} align="center" sx={{ borderLeft: "2px solid rgba(224,224,224,1)", fontWeight: 600, color: "text.secondary" }}>
                  Discover
                </TableCell>
                <TableCell colSpan={2} align="center" sx={{ borderLeft: "2px solid rgba(224,224,224,1)", fontWeight: 600, color: "text.secondary" }}>
                  Fetch
                </TableCell>
                <TableCell colSpan={5} align="center" sx={{ borderLeft: "2px solid rgba(224,224,224,1)", fontWeight: 600, color: "text.secondary" }}>
                  Ingest
                </TableCell>
                <TableCell colSpan={2} align="center" sx={{ borderLeft: "2px solid rgba(224,224,224,1)", fontWeight: 600, color: "text.secondary" }}>
                  Timing
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ borderLeft: "2px solid rgba(224,224,224,1)" }}>
                  <Tooltip title="Total attachment URLs found in the source listing"><span>Disc.</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="URLs already resolved in a prior run — skipped entirely"><span>Seen</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="First-time URLs this run (discovered − seen)"><span>New</span></Tooltip>
                </TableCell>
                <TableCell sx={{ borderLeft: "2px solid rgba(224,224,224,1)", color: "error.main" }}>
                  <Tooltip title="Download failed — retry via region DL button"><span>DL Fail</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Files successfully fetched from source this run"><span>Down.</span></Tooltip>
                </TableCell>
                <TableCell sx={{ borderLeft: "2px solid rgba(224,224,224,1)", color: "warning.main" }}>
                  <Tooltip title="Downloaded but not a PDF — permanent skip"><span>Not PDF</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Downloaded PDFs (downloaded − not PDF)"><span>PDF</span></Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Same content hash already in DB — permanent skip"><span>Hash</span></Tooltip>
                </TableCell>
                <TableCell sx={{ color: "success.main" }}>
                  <Tooltip title="New documents uploaded + inserted to DB"><span>Uploaded</span></Tooltip>
                </TableCell>
                <TableCell sx={{ color: "error.main" }}>
                  <Tooltip title="Upload or DB insert failed — retry via region Up button"><span>Up Fail</span></Tooltip>
                </TableCell>
                <TableCell sx={{ borderLeft: "2px solid rgba(224,224,224,1)" }}>Started</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {countyTasks.map((t) => {
                const isExpanded = expandedTaskId === t.id;
                const stale = isHeartbeatStale(t);
                const canExpand = t.status === "completed" || t.status === "failed";
                const s = buildRunStats(t);
                const durationLabel = formatRunDuration(s.durationSec);
                return (
                  <React.Fragment key={t.id}>
                    <TableRow
                      hover
                      sx={{ cursor: canExpand ? "pointer" : "default", "& > *": { borderBottom: isExpanded ? 0 : undefined } }}
                      onClick={() => canExpand && handleExpandTask(t.id)}
                    >
                      <TableCell sx={{ width: 28, p: 0.5 }}>
                        {canExpand && (
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.region_name}</Typography>
                      </TableCell>
                      <TableCell>{t.source_system}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Chip
                            label={t.status}
                            size="small"
                            color={t.status === "completed" ? "success" : t.status === "running" ? "primary" : t.status === "failed" ? "error" : "default"}
                            variant={t.status === "queued" ? "outlined" : "filled"}
                          />
                          {stale && (
                            <Tooltip title={`Heartbeat stale since ${new Date(t.last_heartbeat_at).toLocaleString()} — task may have crashed`}>
                              <WarningAmber sx={{ fontSize: 16, color: "warning.main" }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      {statCell(s.discovered, { borderLeft: "2px solid rgba(224,224,224,1)", fontWeight: 500 })}
                      {statCell(s.seen)}
                      {statCell(s.newUrls)}
                      {statCell(s.downloadFailed, { borderLeft: "2px solid rgba(224,224,224,1)", color: "error.main" })}
                      {statCell(s.downloaded)}
                      {statCell(s.notPdf, { borderLeft: "2px solid rgba(224,224,224,1)", color: "warning.main" })}
                      {statCell(s.pdfCount)}
                      {statCell(s.hashDup)}
                      {statCell(s.newDocs, { color: "success.main", fontWeight: s.newDocs ? 600 : undefined })}
                      {statCell(s.uploadFailed, { color: "error.main" })}
                      <TableCell sx={{ borderLeft: "2px solid rgba(224,224,224,1)" }}>
                        {t.started_at ? new Date(t.started_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {durationLabel}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={TASK_COL_COUNT} sx={{ py: 0, px: 2, bgcolor: "action.hover" }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2 }}>
                              {loadingManifest ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>
                              ) : manifestData ? (
                                <ScrapingRunStats
                                  task={t}
                                  manifest={manifestData.manifest}
                                  resolvedConfig={manifestData.resolved_config}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">No manifest data available.</Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default ScrapingManager;